import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';

export const ACTIVITY_TYPES = [
    { label: 'Curso', value: 'COURSE' },
    { label: 'Exame de Graduação', value: 'GRADE_EXAM' },
    { label: 'Credenciamento de Árbitro', value: 'JUDGE_ACCREDITATION' },
    { label: 'Credenciamento de Instrutor', value: 'INSTRUCTOR_ACCREDITATION' },
    { label: 'Credenciamento de Examinador', value: 'EXAMINER_ACCREDITATION' },
];

const typeLabel = (value) => ACTIVITY_TYPES.find(t => t.value === value)?.label ?? value;

const COURSE = 'COURSE';
const GRADE_EXAM = 'GRADE_EXAM';

const isCourse = (type) => type === COURSE;
const isGradeExam = (type) => type === GRADE_EXAM;

// Teto de dan do Exame de Graduação: 1º a 7º dan.
const DAN_OPTIONS = [1, 2, 3, 4, 5, 6, 7].map(n => ({ label: `${n}º dan`, value: n }));

// Classes válidas por credenciamento. D é a entrada, A é a mais alta; a grade é
// irregular — os pares ausentes não existem no domínio e o backend recusa (400).
const LICENSE_CLASSES_BY_TYPE = {
    JUDGE_ACCREDITATION: ['D', 'C', 'B', 'A'],
    INSTRUCTOR_ACCREDITATION: ['D', 'C', 'B'],
    EXAMINER_ACCREDITATION: ['D', 'C'],
};

const isAccreditation = (type) => type in LICENSE_CLASSES_BY_TYPE;

const licenseClassOptions = (type) =>
    (LICENSE_CLASSES_BY_TYPE[type] || []).map(c => ({ label: `Classe ${c}`, value: c }));

// De onde vem o preço quando a atividade NÃO tem lote (tudo que não é Curso).
// GRADE_EXAM lê da tabela de taxas por dan; os três credenciamentos, da tabela
// de taxas de licença por classe.
const priceOriginText = (type) => {
    if (type === 'GRADE_EXAM') return 'Valor definido na tabela de taxas por dan';
    return 'Valor definido na tabela de taxas de licença por classe';
};

const parseDate = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// yyyy-MM-dd -> dd/mm/yyyy para exibição.
const formatDateBR = (str) => {
    if (!str) return '';
    const [y, m, d] = str.split('-');
    return `${d}/${m}/${y}`;
};

const emptyBatch = () => ({ validUntil: '', price: 0 });
const emptyActivity = { activityType: '', priceBatches: [] };

function vigenteBatch(priceBatches) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (priceBatches || [])
        .filter(b => b.validUntil && parseDate(b.validUntil) >= today)
        .sort((a, b) => parseDate(a.validUntil) - parseDate(b.validUntil))[0] ?? null;
}

function batchErrors(batches) {
    if (!batches || batches.length === 0) return ['Adicione ao menos um lote'];
    const errs = [];
    batches.forEach((b, i) => {
        if (!b.price || b.price <= 0) errs.push(`Lote ${i + 1}: preço inválido`);
        if (!b.validUntil) errs.push(`Lote ${i + 1}: data obrigatória`);
    });
    const dates = batches.map(b => b.validUntil).filter(Boolean);
    if (new Set(dates).size < dates.length) errs.push('Datas duplicadas nos lotes');
    return errs;
}

export function TabActivities({ activities, onActivitiesChange }) {
    const [dialog, setDialog] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [activity, setActivity] = useState({ ...emptyActivity, priceBatches: [emptyBatch()] });
    const [submitted, setSubmitted] = useState(false);

    const openAdd = () => {
        setActivity({ ...emptyActivity, priceBatches: [emptyBatch()] });
        setEditIndex(null);
        setSubmitted(false);
        setDialog(true);
    };

    const openEdit = (a, idx) => {
        setActivity({ ...a, priceBatches: (a.priceBatches || [emptyBatch()]).map(b => ({ ...b })) });
        setEditIndex(idx);
        setSubmitted(false);
        setDialog(true);
    };

    const save = () => {
        setSubmitted(true);
        if (!activity.activityType) return;
        const course = isCourse(activity.activityType);
        const exam = isGradeExam(activity.activityType);
        const accreditation = isAccreditation(activity.activityType);
        // Validação por tipo, espelhando o contrato (violação = 400 no backend).
        if (course && batchErrors(activity.priceBatches).length > 0) return;
        if (exam && (!activity.registrationDeadline || !activity.maxDan)) return;
        if (accreditation && (!activity.registrationDeadline || !activity.maxLicenseClass)) return;

        // Payload leva SOMENTE os campos do tipo; os demais são removidos (delete),
        // não enviados como null — campo de outro tipo faz o backend recusar (400).
        const payload = { ...activity };
        if (course) {
            delete payload.registrationDeadline;
            delete payload.maxDan;
            delete payload.maxLicenseClass;
        } else if (exam) {
            delete payload.priceBatches;
            delete payload.maxLicenseClass;
        } else {
            delete payload.priceBatches;
            delete payload.maxDan;
        }
        const list = [...activities];
        if (editIndex !== null) {
            list[editIndex] = payload;
        } else {
            list.push(payload);
        }
        onActivitiesChange(list);
        setDialog(false);
    };

    const remove = (idx) => {
        onActivitiesChange(activities.filter((_, i) => i !== idx));
    };

    const updateBatch = (bi, field, value) => {
        setActivity(prev => ({
            ...prev,
            priceBatches: prev.priceBatches.map((b, i) => i === bi ? { ...b, [field]: value } : b),
        }));
    };

    const addBatch = () => {
        setActivity(prev => ({ ...prev, priceBatches: [...prev.priceBatches, emptyBatch()] }));
    };

    const removeBatch = (bi) => {
        setActivity(prev => ({ ...prev, priceBatches: prev.priceBatches.filter((_, i) => i !== bi) }));
    };

    const priceTemplate = (row) => {
        // Sem lote (tudo que não é Curso): origem do valor + teto + prazo de inscrição.
        if (!isCourse(row.activityType)) {
            const parts = [priceOriginText(row.activityType)];
            if (isGradeExam(row.activityType) && row.maxDan) parts.push(`até ${row.maxDan}º dan`);
            if (isAccreditation(row.activityType) && row.maxLicenseClass) parts.push(`até classe ${row.maxLicenseClass}`);
            if (row.registrationDeadline) parts.push(`inscrições até ${formatDateBR(row.registrationDeadline)}`);
            return <span style={{ color: '#888' }}>{parts.join(' · ')}</span>;
        }
        const batch = vigenteBatch(row.priceBatches);
        if (!batch) return <span style={{ color: '#888' }}>Encerrado</span>;
        return `R$ ${Number(batch.price).toFixed(2)} · até ${formatDateBR(batch.validUntil)}`;
    };

    const actionTemplate = (row, { rowIndex }) => (
        <>
            <Button icon="pi pi-pencil"
                className="p-button-rounded p-button-success mr-2 p-button-sm"
                onClick={() => openEdit(row, rowIndex)} />
            <Button icon="pi pi-trash"
                className="p-button-rounded p-button-danger p-button-sm"
                onClick={() => remove(rowIndex)} />
        </>
    );

    const batches = activity.priceBatches || [];
    const course = isCourse(activity.activityType);
    const exam = isGradeExam(activity.activityType);
    const accreditation = isAccreditation(activity.activityType);
    const needsDeadline = exam || accreditation;
    const batchErrs = submitted && course ? batchErrors(batches) : [];

    const dialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setDialog(false)} />
            <Button label="Confirmar" icon="pi pi-check" onClick={save} />
        </>
    );

    return (
        <>
            <div className="flex justify-content-end mb-3">
                <Button label="Adicionar Atividade" icon="pi pi-plus"
                    className="p-button-sm" onClick={openAdd} />
            </div>
            <DataTable value={activities} emptyMessage="Nenhuma atividade cadastrada">
                <Column header="Tipo" body={(row) => typeLabel(row.activityType)} />
                <Column header="Preço / Lote vigente" body={priceTemplate} />
                <Column body={actionTemplate} style={{ width: '9rem' }} />
            </DataTable>

            <Dialog visible={dialog} style={{ width: '540px' }}
                header={editIndex !== null ? 'Editar Atividade' : 'Nova Atividade'}
                modal className="p-fluid" footer={dialogFooter}
                onHide={() => setDialog(false)}>
                <div className="formgrid grid">
                    <div className="field col-12">
                        <label>Tipo *</label>
                        <Dropdown value={activity.activityType} options={ACTIVITY_TYPES}
                            onChange={(e) => setActivity(prev => {
                                // Trocar o tipo limpa todo campo que não se aplica ao tipo novo:
                                // lote só em Curso, maxDan só em Exame, classe só em Credenciamento.
                                // Campo de outro tipo no payload faz o backend recusar (400).
                                const validClasses = LICENSE_CLASSES_BY_TYPE[e.value] || [];
                                return {
                                    ...prev,
                                    activityType: e.value,
                                    priceBatches: e.value === COURSE ? [emptyBatch()] : [],
                                    registrationDeadline: '',
                                    maxDan: null,
                                    // Entre credenciamentos, mantém a classe só se ela existir na
                                    // grade do tipo novo (ex.: Árbitro classe A → Examinador limpa).
                                    maxLicenseClass: validClasses.includes(prev.maxLicenseClass) ? prev.maxLicenseClass : '',
                                };
                            })}
                            className={submitted && !activity.activityType ? 'p-invalid' : ''} />
                        {submitted && !activity.activityType && <small className="p-error">Obrigatório</small>}
                    </div>

                    {course && (
                    <div className="field col-12">
                        <div className="flex align-items-center justify-content-between mb-2">
                            <label className="mb-0">Lotes *</label>
                            <Button label="Adicionar lote" icon="pi pi-plus"
                                className="p-button-sm p-button-outlined"
                                onClick={addBatch} type="button" />
                        </div>

                        {batchErrs.length > 0 && (
                            <div className="mb-2">
                                {batchErrs.map((e, i) => (
                                    <small key={i} className="p-error block">{e}</small>
                                ))}
                            </div>
                        )}

                        <DataTable value={batches} emptyMessage="Adicione ao menos um lote">
                            <Column header="Preço (R$)" body={(row, { rowIndex: bi }) => (
                                <InputNumber
                                    value={row.price}
                                    onValueChange={(e) => updateBatch(bi, 'price', e.value)}
                                    mode="decimal" minFractionDigits={2} maxFractionDigits={2} min={0.01}
                                    className={submitted && (!row.price || row.price <= 0) ? 'p-invalid' : ''}
                                />
                            )} />
                            <Column header="Vale até" body={(row, { rowIndex: bi }) => (
                                <Calendar
                                    value={parseDate(row.validUntil)}
                                    onChange={(e) => updateBatch(bi, 'validUntil', formatDate(e.value))}
                                    dateFormat="dd/mm/yy" showIcon
                                    className={submitted && !row.validUntil ? 'p-invalid' : ''}
                                />
                            )} />
                            <Column style={{ width: '4rem' }} body={(row, { rowIndex: bi }) => (
                                batches.length > 1 && (
                                    <Button icon="pi pi-trash"
                                        className="p-button-rounded p-button-danger p-button-sm"
                                        onClick={() => removeBatch(bi)} type="button" />
                                )
                            )} />
                        </DataTable>
                    </div>
                    )}

                    {needsDeadline && (
                        <div className="field col-12">
                            <label>Inscrições até *</label>
                            <Calendar
                                value={parseDate(activity.registrationDeadline)}
                                onChange={(e) => setActivity(prev => ({ ...prev, registrationDeadline: formatDate(e.value) }))}
                                dateFormat="dd/mm/yy" showIcon
                                className={submitted && !activity.registrationDeadline ? 'p-invalid' : ''}
                            />
                            {submitted && !activity.registrationDeadline && <small className="p-error">Obrigatório</small>}
                        </div>
                    )}

                    {exam && (
                        <div className="field col-12">
                            <label>Examina até o dan *</label>
                            <Dropdown value={activity.maxDan} options={DAN_OPTIONS}
                                placeholder="Selecione o dan"
                                onChange={(e) => setActivity(prev => ({ ...prev, maxDan: e.value }))}
                                className={submitted && !activity.maxDan ? 'p-invalid' : ''} />
                            {submitted && !activity.maxDan && <small className="p-error">Obrigatório</small>}
                        </div>
                    )}

                    {accreditation && (
                        <div className="field col-12">
                            <label>Credencia até a classe *</label>
                            <Dropdown value={activity.maxLicenseClass} options={licenseClassOptions(activity.activityType)}
                                placeholder="Selecione a classe"
                                onChange={(e) => setActivity(prev => ({ ...prev, maxLicenseClass: e.value }))}
                                className={submitted && !activity.maxLicenseClass ? 'p-invalid' : ''} />
                            {submitted && !activity.maxLicenseClass && <small className="p-error">Obrigatório</small>}
                        </div>
                    )}

                    {activity.activityType && !course && (
                        <div className="field col-12">
                            <label className="mb-0">Preço</label>
                            <div className="p-3 mt-2 border-round surface-100 text-700">
                                <i className="pi pi-info-circle mr-2" />
                                {priceOriginText(activity.activityType)}
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>
        </>
    );
}
